/**
 * 검차 스케줄 캘린더
 * 
 * 관리자: 전체 평가사 일정 확인 + 오더 클릭 시 상세 이동
 * 평가사: 내 일정만 확인
 * 
 * Props:
 *   orders: [] - 오더 목록
 *   inspectors: [] - 평가사 목록 (관리자용)
 *   onSelectOrder: (orderId) => void - 오더 클릭 콜백
 *   inspectorFilter: number|null - 특정 평가사만 필터 (null=전체)
 *   onBack: () => void
 */
import React, { useState } from 'react';

const STATUS_COLORS = {
  open:       { color: '#3B8BD4', bg: '#E6F1FB', label: '대기' },
  taken:      { color: '#1D9E75', bg: '#E1F5EE', label: '수락' },
  inspecting: { color: '#BA7517', bg: '#FAEEDA', label: '검차중' },
  completed:  { color: '#534AB7', bg: '#EEEDFE', label: '완료' },
  decided:    { color: '#D85A30', bg: '#FAECE7', label: '매입' },
  rejected:   { color: '#A32D2D', bg: '#FCEBEB', label: '불가' },
};

const DAY_NAMES = ['일', '월', '화', '수', '목', '금', '토'];
const TODAY = new Date();
TODAY.setHours(0, 0, 0, 0);

function formatDateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function isSameDay(d1, d2) {
  return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
}

export default function ScheduleCalendar({ orders = [], inspectors = [], onSelectOrder, inspectorFilter = null, onBack }) {
  const [viewMode, setViewMode] = useState('month');
  const [offset, setOffset] = useState(0);
  const [selectedDate, setSelectedDate] = useState(null);
  const [filterInspector, setFilterInspector] = useState(inspectorFilter);

  // 필터링된 오더
  const filteredOrders = orders.filter(o => {
    if (filterInspector && o.inspector_id !== filterInspector) return false;
    return true;
  });

  // 날짜별 오더 맵
  const ordersByDate = {};
  filteredOrders.forEach(o => {
    if (!o.inspect_date) return;
    if (!ordersByDate[o.inspect_date]) ordersByDate[o.inspect_date] = [];
    ordersByDate[o.inspect_date].push(o);
  });

  // ─── 주간 뷰 날짜 계산 ───
  const getWeekDays = () => {
    const start = new Date(TODAY);
    start.setDate(TODAY.getDate() - TODAY.getDay() + 1 + offset * 7); // 월요일 시작
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  };

  // ─── 월간 뷰 날짜 계산 ───
  const getMonthDays = () => {
    const current = new Date(TODAY.getFullYear(), TODAY.getMonth() + offset, 1);
    const year = current.getFullYear();
    const month = current.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const lastDate = new Date(year, month + 1, 0).getDate();
    
    const days = [];
    // 이전 달 빈칸 (일요일 시작)
    for (let i = 0; i < firstDay; i++) {
      const d = new Date(year, month, -firstDay + i + 1);
      days.push({ date: d, isOtherMonth: true });
    }
    // 이번 달
    for (let i = 1; i <= lastDate; i++) {
      days.push({ date: new Date(year, month, i), isOtherMonth: false });
    }
    // 다음 달 채우기 (6주)
    while (days.length < 42) {
      const d = new Date(year, month + 1, days.length - firstDay - lastDate + 1);
      days.push({ date: d, isOtherMonth: true });
    }
    return days;
  };

  // 헤더 텍스트
  const getHeaderText = () => {
    if (viewMode === 'week') {
      const days = getWeekDays();
      const start = days[0];
      const end = days[6];
      if (start.getMonth() === end.getMonth()) {
        return `${start.getFullYear()}년 ${start.getMonth() + 1}월 ${start.getDate()}일 ~ ${end.getDate()}일`;
      }
      return `${start.getMonth() + 1}/${start.getDate()} ~ ${end.getMonth() + 1}/${end.getDate()}`;
    } else {
      const current = new Date(TODAY.getFullYear(), TODAY.getMonth() + offset, 1);
      return `${current.getFullYear()}년 ${current.getMonth() + 1}월`;
    }
  };

  // 선택된 날짜의 오더
  const selectedDateOrders = selectedDate ? (ordersByDate[formatDateKey(selectedDate)] || []) : [];

  // 오늘 일정 요약
  const todayKey = formatDateKey(TODAY);
  const todayOrders = ordersByDate[todayKey] || [];

  return (
    <div style={{ maxWidth: 640, margin: '0 auto', padding: '16px 12px' }}>
      {/* 헤더 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {onBack && (
            <button onClick={onBack} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 15, color: '#3B8BD4', padding: 0, fontWeight: 600,
            }}>←</button>
          )}
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#111' }}>검차 스케줄</h2>
        </div>
        {/* 주간/월간 토글 */}
        <div style={{ display: 'flex', gap: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid #e0dfda' }}>
          {[['week', '주간'], ['month', '월간']].map(([key, label]) => (
            <button key={key} onClick={() => { setViewMode(key); setOffset(0); }} style={{
              padding: '6px 14px', border: 'none', cursor: 'pointer',
              fontSize: 12, fontWeight: viewMode === key ? 700 : 400,
              color: viewMode === key ? '#3B8BD4' : '#888',
              background: viewMode === key ? '#E6F1FB' : '#fff',
            }}>{label}</button>
          ))}
        </div>
      </div>

      {/* 오늘 요약 */}
      {todayOrders.length > 0 && (
        <div style={{
          background: '#E6F1FB', borderRadius: 12, padding: '10px 14px',
          marginBottom: 12, border: '1px solid #B5D4F4',
        }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#185FA5' }}>
            오늘 검차 {todayOrders.length}건
          </span>
          <span style={{ fontSize: 12, color: '#3B8BD4', marginLeft: 8 }}>
            {todayOrders.map(o => `${o.car_model?.split(' ')[0]} ${o.inspect_time}`).join(', ')}
          </span>
        </div>
      )}

      {/* 평가사 필터 (관리자용) */}
      {inspectors.length > 0 && (
        <div style={{ display: 'flex', gap: 4, marginBottom: 12, overflowX: 'auto', paddingBottom: 4 }}>
          <button onClick={() => setFilterInspector(null)} style={{
            padding: '5px 12px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
            border: !filterInspector ? '1.5px solid #3B8BD4' : '1px solid #e0dfda',
            background: !filterInspector ? '#E6F1FB' : '#fff',
            color: !filterInspector ? '#185FA5' : '#888',
            fontWeight: !filterInspector ? 600 : 400,
            whiteSpace: 'nowrap', flexShrink: 0,
          }}>전체</button>
          {inspectors.map(ins => (
            <button key={ins.id} onClick={() => setFilterInspector(ins.id)} style={{
              padding: '5px 12px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
              border: filterInspector === ins.id ? '1.5px solid #3B8BD4' : '1px solid #e0dfda',
              background: filterInspector === ins.id ? '#E6F1FB' : '#fff',
              color: filterInspector === ins.id ? '#185FA5' : '#888',
              fontWeight: filterInspector === ins.id ? 600 : 400,
              whiteSpace: 'nowrap', flexShrink: 0,
            }}>{ins.name}</button>
          ))}
        </div>
      )}

      {/* 네비게이션 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <button onClick={() => setOffset(o => o - 1)} style={{
          background: 'none', border: '1px solid #e0dfda', borderRadius: 8,
          padding: '6px 12px', cursor: 'pointer', fontSize: 14, color: '#666',
        }}>◀</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#222' }}>{getHeaderText()}</span>
          {offset !== 0 && (
            <button onClick={() => setOffset(0)} style={{
              background: '#E6F1FB', border: 'none', borderRadius: 6,
              padding: '3px 8px', cursor: 'pointer', fontSize: 11, color: '#3B8BD4', fontWeight: 600,
            }}>오늘</button>
          )}
        </div>
        <button onClick={() => setOffset(o => o + 1)} style={{
          background: 'none', border: '1px solid #e0dfda', borderRadius: 8,
          padding: '6px 12px', cursor: 'pointer', fontSize: 14, color: '#666',
        }}>▶</button>
      </div>

      {/* ─── 주간 뷰 ─── */}
      {viewMode === 'week' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 12 }}>
          {getWeekDays().map((date, i) => {
            const key = formatDateKey(date);
            const dayOrders = ordersByDate[key] || [];
            const isToday = isSameDay(date, TODAY);
            const isSelected = selectedDate && isSameDay(date, selectedDate);
            const isWeekend = date.getDay() === 0 || date.getDay() === 6;

            return (
              <div
                key={i}
                onClick={() => setSelectedDate(isSelected ? null : date)}
                style={{
                  padding: '8px 4px', borderRadius: 10, minHeight: 100, cursor: 'pointer',
                  background: isSelected ? '#E6F1FB' : isToday ? '#FAEEDA' : '#fff',
                  border: isSelected ? '1.5px solid #3B8BD4' : isToday ? '1.5px solid #EF9F27' : '1px solid #eee',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                {/* 요일 + 날짜 */}
                <div style={{ textAlign: 'center', marginBottom: 6 }}>
                  <div style={{ fontSize: 10, color: isWeekend ? '#E24B4A' : '#aaa', fontWeight: 500 }}>
                    {DAY_NAMES[date.getDay()]}
                  </div>
                  <div style={{
                    fontSize: 16, fontWeight: isToday ? 800 : 600,
                    color: isToday ? '#BA7517' : isWeekend ? '#E24B4A' : '#222',
                  }}>
                    {date.getDate()}
                  </div>
                </div>

                {/* 오더 태그 */}
                {dayOrders.slice(0, 3).map(o => {
                  const st = STATUS_COLORS[o.status] || STATUS_COLORS.open;
                  return (
                    <div key={o.id} style={{
                      fontSize: 9, padding: '2px 4px', borderRadius: 4, marginBottom: 2,
                      background: st.bg, color: st.color, fontWeight: 600,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {o.inspect_time === '오전' ? '오전' : '오후'} {o.car_model?.split(' ')[0]?.slice(0, 4)}
                    </div>
                  );
                })}
                {dayOrders.length > 3 && (
                  <div style={{ fontSize: 9, color: '#888', textAlign: 'center' }}>+{dayOrders.length - 3}</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ─── 월간 뷰 ─── */}
      {viewMode === 'month' && (
        <div>
          {/* 요일 헤더 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
            {DAY_NAMES.map((name, i) => (
              <div key={i} style={{
                textAlign: 'center', fontSize: 11, fontWeight: 600,
                color: i === 0 || i === 6 ? '#E24B4A' : '#888', padding: '4px 0',
              }}>{name}</div>
            ))}
          </div>
          {/* 날짜 그리드 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 12 }}>
            {getMonthDays().map(({ date, isOtherMonth }, i) => {
              const key = formatDateKey(date);
              const dayOrders = ordersByDate[key] || [];
              const isToday = isSameDay(date, TODAY);
              const isSelected = selectedDate && isSameDay(date, selectedDate);
              const isWeekend = date.getDay() === 0 || date.getDay() === 6;

              return (
                <div
                  key={i}
                  onClick={() => setSelectedDate(isSelected ? null : date)}
                  style={{
                    padding: '4px 2px', borderRadius: 8, minHeight: 48, cursor: 'pointer',
                    background: isSelected ? '#E6F1FB' : isToday ? '#FAEEDA' : 'transparent',
                    border: isSelected ? '1.5px solid #3B8BD4' : isToday ? '1.5px solid #EF9F27' : '1px solid transparent',
                    opacity: isOtherMonth ? 0.3 : 1,
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  <div style={{
                    textAlign: 'center', fontSize: 13,
                    fontWeight: isToday ? 800 : 500,
                    color: isToday ? '#BA7517' : isWeekend ? '#E24B4A' : '#444',
                    marginBottom: 2,
                  }}>
                    {date.getDate()}
                  </div>
                  {/* 오더 도트 */}
                  {dayOrders.length > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                      {dayOrders.slice(0, 4).map(o => {
                        const st = STATUS_COLORS[o.status] || STATUS_COLORS.open;
                        return <div key={o.id} style={{ width: 6, height: 6, borderRadius: '50%', background: st.color }} />;
                      })}
                      {dayOrders.length > 4 && (
                        <span style={{ fontSize: 8, color: '#888' }}>+{dayOrders.length - 4}</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ─── 선택된 날짜 상세 ─── */}
      {selectedDate && (
        <div style={{
          background: '#fff', borderRadius: 14, border: '1px solid #eee',
          padding: 16, marginBottom: 12,
        }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#222', marginBottom: 10 }}>
            {selectedDate.getMonth() + 1}월 {selectedDate.getDate()}일 ({DAY_NAMES[selectedDate.getDay()]})
            {selectedDateOrders.length > 0 && (
              <span style={{ fontSize: 13, fontWeight: 400, color: '#888', marginLeft: 8 }}>
                {selectedDateOrders.length}건
              </span>
            )}
          </div>

          {selectedDateOrders.length === 0 && (
            <div style={{ textAlign: 'center', padding: '20px 0', color: '#ccc', fontSize: 13 }}>
              예정된 검차가 없습니다
            </div>
          )}

          {selectedDateOrders.map(o => {
            const st = STATUS_COLORS[o.status] || STATUS_COLORS.open;
            return (
              <div
                key={o.id}
                onClick={() => onSelectOrder?.(o.id)}
                style={{
                  padding: '12px 14px', borderRadius: 10,
                  border: '1px solid #eee', marginBottom: 6,
                  cursor: onSelectOrder ? 'pointer' : 'default',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      display: 'inline-block', padding: '2px 8px', borderRadius: 5,
                      fontSize: 11, fontWeight: 600, color: st.color, background: st.bg,
                    }}>{o.inspect_time}</span>
                    <span style={{ fontSize: 15, fontWeight: 700, color: '#111' }}>{o.car_model}</span>
                  </div>
                  <span style={{
                    padding: '2px 8px', borderRadius: 5, fontSize: 10,
                    fontWeight: 600, color: st.color, background: st.bg,
                  }}>{st.label}</span>
                </div>
                <div style={{ fontSize: 12, color: '#888', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span>{o.location}</span>
                  {o.location && (
                    <a href={'https://map.naver.com/v5/search/' + encodeURIComponent(o.location + ' ' + (o.location_detail || ''))}
                      target="_blank" rel="noopener noreferrer"
                      onClick={function(e) { e.stopPropagation(); }}
                      style={{ fontSize: 11, color: '#1EC800', fontWeight: 600, textDecoration: 'none' }}>지도</a>
                  )}
                  {o.inspector_name && <span>검차: {o.inspector_name}</span>}
                  <span>{(o.fee || 0).toLocaleString()}원</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 범례 */}
      <div style={{
        display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap',
        padding: '8px 0', fontSize: 10, color: '#888',
      }}>
        {Object.entries(STATUS_COLORS).slice(0, 4).map(([key, val]) => (
          <span key={key} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: val.color }} />
            {val.label}
          </span>
        ))}
      </div>
    </div>
  );
}
